import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Flame, Star, Clock, RotateCcw } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { useProgressStore } from '@/stores/progress.store'
import { computeTotalXp, computeLevel, computeStreak } from '@/lib/xp'

const CHAPTER_COMPANY: Record<string, string> = {
  'chapter-1': 'Innotek Systems Inc.',
  'chapter-2': 'Blink.io',
}

function formatAlgoName(id: string) {
  return id.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ')
}

function formatMs(ms: number) {
  return ms >= 60000
    ? `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`
    : `${(ms / 1000).toFixed(1)}s`
}

function Avatar({ url, name }: { url?: string | null; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="w-16 h-16 rounded-full object-cover border-2 border-arcade-border"
      />
    )
  }
  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div className="w-16 h-16 rounded-full bg-arcade-accent flex items-center justify-center border-2 border-arcade-border">
      <span className="text-white font-bold text-lg font-mono">{initials || '?'}</span>
    </div>
  )
}

function StarRow({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3].map((n) => (
        <Star
          key={n}
          size={11}
          className={n <= count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700 fill-gray-700'}
        />
      ))}
    </div>
  )
}

export default function Profile() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { progressMap, fetchProgress } = useProgressStore()

  useEffect(() => {
    if (user?.id) fetchProgress(user.id)
  }, [user?.id, fetchProgress])

  if (!user) {
    return (
      <main className="min-h-screen bg-arcade-bg flex flex-col items-center justify-center text-white px-4 gap-4">
        <p className="text-gray-400">Sign in to see your profile.</p>
        <button onClick={() => navigate('/')} className="text-sm text-arcade-accent hover:underline">
          ← Back to home
        </button>
      </main>
    )
  }

  const meta = user.user_metadata ?? {}
  const displayName: string =
    meta.full_name ?? meta.name ?? meta.user_name ?? user.email ?? 'Player'
  const avatarUrl: string | null = meta.avatar_url ?? null

  const totalXp = computeTotalXp(progressMap)
  const { level, xpInLevel, xpToNextLevel, xpProgress } = computeLevel(totalXp)
  const streak = computeStreak(progressMap)
  const completed = Object.keys(progressMap).length

  const sortedProgress = Object.values(progressMap).sort((a, b) =>
    (a.completed_at ?? '').localeCompare(b.completed_at ?? '')
  )

  return (
    <main className="min-h-screen bg-arcade-bg text-white">
      <header className="border-b border-arcade-border px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/games')}
          className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-lg font-bold">Profile</h1>
      </header>

      <div className="max-w-xl mx-auto px-4 py-8 space-y-5">
        {/* Identity */}
        <div className="bg-arcade-surface border border-arcade-border rounded-xl p-5 flex items-center gap-4">
          <Avatar url={avatarUrl} name={displayName} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-lg truncate">{displayName}</p>
            <p className="text-sm text-gray-400 truncate">{user.email}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="text-xs text-gray-500 font-mono">Level</p>
            <p className="text-2xl font-mono font-bold text-arcade-neon">{level}</p>
          </div>
        </div>

        {/* XP bar */}
        <div className="bg-arcade-surface border border-arcade-border rounded-xl p-4 space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-xs font-mono text-gray-500">XP to Level {level + 1}</p>
            <p className="text-xs font-mono text-arcade-neon">
              {xpInLevel} / {xpToNextLevel}
            </p>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-arcade-neon rounded-full transition-all duration-500"
              style={{ width: `${xpProgress * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 font-mono">{totalXp} total XP</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Completed', value: completed, color: 'text-white' },
            {
              label: 'Streak',
              value: streak,
              color: streak > 0 ? 'text-orange-400' : 'text-white',
              icon: streak > 0 ? <Flame size={14} className="text-orange-400" /> : null,
            },
            { label: 'Total XP', value: totalXp, color: 'text-arcade-neon' },
          ].map(({ label, value, color, icon }) => (
            <div
              key={label}
              className="bg-arcade-surface border border-arcade-border rounded-xl p-4 text-center"
            >
              <p className="text-xs text-gray-500 font-mono mb-1">{label}</p>
              <div className={`flex items-center justify-center gap-1 text-2xl font-mono font-bold ${color}`}>
                {icon}
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Challenge history */}
        <div>
          <p className="text-xs font-mono text-gray-500 mb-3 uppercase tracking-widest">
            Completed Challenges
          </p>
          {sortedProgress.length === 0 ? (
            <div className="bg-arcade-surface border border-arcade-border rounded-xl p-6 text-center">
              <p className="text-gray-500 text-sm">No challenges completed yet.</p>
              <button
                onClick={() => navigate('/play/algoquest')}
                className="mt-3 text-sm text-arcade-accent hover:underline"
              >
                Start AlgoQuest →
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {sortedProgress.map((row) => (
                <div
                  key={row.algo_id}
                  className="bg-arcade-surface border border-arcade-border rounded-xl px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-white text-sm">{formatAlgoName(row.algo_id)}</p>
                    <p className="text-xs text-gray-500 font-mono">
                      {CHAPTER_COMPANY[row.chapter_id] ?? row.chapter_id}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StarRow count={row.stars} />
                    {row.best_time_ms != null && (
                      <span className="flex items-center gap-1 text-xs text-gray-500 font-mono">
                        <Clock size={10} />
                        {formatMs(row.best_time_ms)}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-xs text-gray-600 font-mono">
                      <RotateCcw size={10} />
                      {row.attempts}×
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
